import React, { useState } from 'react';
import ProductAPI from '../services/API';

const ProductForm = ({ formData, handleChange, handleSubmit, isEditing }) => {
  const [previewUrls, setPreviewUrls] = useState(() => {
    if (isEditing && formData.photos) {
      return formData.photos.map(photoUrl => ({
        url: photoUrl,
        isExisting: true
      }));
    }
    return [];
  });

  const productTypes = [
    "Ancient Coin Pendant",
    "Necklace",
    "Bracelet",
    "Ring",
    "Earring",
    "Other"
  ];

  // Add wrapper for handleSubmit to clear photos after submission
  const onSubmit = async (e) => {
    await handleSubmit(e);
    setPreviewUrls([]); // Clear preview URLs after submission
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Create preview URLs for the new files
    const newPreviewUrls = files.map(file => ({
      url: URL.createObjectURL(file),
      isExisting: false
    }));
    
    // Append new preview URLs to existing ones
    setPreviewUrls(prevUrls => [...prevUrls, ...newPreviewUrls]);
    
    // Append new files to existing photos
    handleChange({
      target: {
        name: 'photos',
        value: [...(formData.photos || []), ...files]
      }
    });
  };

  const handleRemoveImage = async (index) => {
    const urlToRemove = previewUrls[index];
    
    if (urlToRemove.isExisting) {
        try {
            // Extract filename from the URL
            const filename = urlToRemove.url.split('/').pop();
            
            // Call API to delete the image
            await ProductAPI.deleteProductImage(formData.id, filename);
        } catch (error) {
            console.error('Error deleting image:', error);
            return; // Don't proceed if deletion failed
        }
    } else {
        // For new uploads, revoke the object URL
        URL.revokeObjectURL(urlToRemove.url);
    }

    // Update preview URLs
    setPreviewUrls(prevUrls => prevUrls.filter((_, i) => i !== index));

    // Update form data
    const newPhotos = formData.photos.filter((_, i) => i !== index);
    handleChange({
        target: {
            name: 'photos',
            value: newPhotos
        }
    });
  };

  // Update cleanup to only revoke non-existing photo URLs
  React.useEffect(() => {
    return () => {
      previewUrls
        .filter(item => !item.isExisting)
        .forEach(item => URL.revokeObjectURL(item.url));
    };
  }, [previewUrls]);

  return (
    <form onSubmit={onSubmit} className="space-y-4 bg-white p-6 rounded-lg shadow">
      <h2 className="text-xl font-semibold mb-4">
        {isEditing ? 'Edit Product' : 'Add New Product'}
      </h2>
      
      <div>
        <label className="block mb-1">Name</label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className="w-full border p-2 rounded"
          required
        />
      </div>

      <div>
        <label className="block mb-1">Type</label>
        <select
          name="type"
          value={formData.type || ''}
          onChange={handleChange}
          className="w-full border p-2 rounded"
          required
        >
          <option value="">Select a type</option>
          {productTypes.map(type => (
            <option key={type} value={type}>
              {type.replace('_', ' ')}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block mb-1">Price</label>
        <input
          type="number"
          name="price"
          value={formData.price}
          onChange={handleChange}
          className="w-full border p-2 rounded"
          required
        />
      </div>

      <div>
        <label className="block mb-1">Description</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          className="w-full border p-2 rounded"
          rows="4"
          required
        />
      </div>

      <div>
        <label className="block mb-1">Photos</label>
        <input
          type="file"
          name="photos"
          onChange={handleFileChange}
          multiple
          accept="image/*"
          className="w-full border p-2 rounded"
        />
        
        <div className="mt-4 grid grid-cols-4 gap-4">
          {previewUrls.map((item, index) => (
            <div key={index} className="relative">
              <img
                src={item.url}
                alt={`Preview ${index + 1}`}
                className="w-full h-32 object-cover rounded"
              />
              <button
                type="button"
                onClick={() => handleRemoveImage(index)}
                className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center -mt-2 -mr-2"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      </div>

      <button
        type="submit"
        className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
      >
        {isEditing ? 'Update Product' : 'Add Product'}
      </button>
    </form>
  );
};

export default ProductForm;